import { Component, EventEmitter, OnDestroy, Output } from '@angular/core'

import { BatchPaymentRow, PaymentBatchStateService } from '@modules/private/payment/data/services/view/payment-batch-state.service'
import { ApiApplicationService } from '@modules/private/application/data/services/api/api-application.service'
import { ApiPaymentBatchService } from '@modules/private/payment/data/services/api/api-payment-batch.service'

/**
 * Paso 6 · La aplicación. **Segundo punto de no retorno.** Y la tarjeta de avisos (D2).
 *
 * Aplica lo que se acaba de previsualizar, uno detrás de otro y por la misma razón que el paso
 * anterior: cuarenta transacciones concurrentes contra el clúster que atiende a toda la plataforma.
 *
 * Cada pago viaja con la **huella de estado** de SU previsualización. Si el arrendamiento cambió
 * entretanto, el motor rechaza ese pago y sigue con los demás: es exactamente lo que tiene que pasar,
 * porque el impacto que el operador aprobó ya no es el que se escribiría.
 *
 * El aviso al cliente viaja apagado en cada aplicación (`notify: false`) y se manda al final, una vez
 * por cliente y para todo el lote (D2). Si fuera por pago, a quien pagó tres contratos le llegarían
 * tres «gracias por su pago» y parecería un error del sistema.
 */
@Component({
	selector: 'app-step6-aplicacion',
	templateUrl: './step6-aplicacion.component.html',
	styleUrls: ['./step6-aplicacion.component.scss'],
})
export class Step6AplicacionComponent implements OnDestroy {
	@Output() onBack = new EventEmitter<void>()
	@Output() onExit = new EventEmitter<void>()

	confirmado = false
	aplicando = false
	hechos = 0
	private cancelado = false
	cancelando = false

	/** El aviso es todo o nada y hay que pedirlo: no sale solo al terminar de aplicar. */
	confirmadoAviso = false
	avisando = false
	avisoError = ''

	constructor(
		public readonly state: PaymentBatchStateService,
		private readonly apiApplication: ApiApplicationService,
		private readonly apiBatch: ApiPaymentBatchService
	) {}

	ngOnDestroy(): void {
		this.cancelado = true
	}

	/**
	 * Los pagos de esta corrida. **Es una foto, no una vista viva.**
	 *
	 * `state.previewedRows` filtra por «le queda algo por aplicar», y aplicar deja eso en cero: cada
	 * pago que se aplicaba DESAPARECÍA de la lista mientras el bucle corría. El avance contaba «1 de 1»
	 * con dos pagos en curso, el resumen final salía vacío —así que no se enseñaba— y la pantalla
	 * volvía a ofrecer «aplicar 1 pago» como si no hubiera pasado nada, con el dinero ya movido.
	 * Verificado en Chrome: dos pagos, uno aplicado, cero rastro en pantalla.
	 */
	private objetivoCorrida: BatchPaymentRow[] = []

	get objetivo(): BatchPaymentRow[] {
		return this.objetivoCorrida.length ? this.objetivoCorrida : this.state.previewedRows
	}

	get total(): number {
		return this.objetivo.length
	}

	get progreso(): number {
		return this.total ? Math.round((this.hechos / this.total) * 100) : 0
	}

	get aplicados(): BatchPaymentRow[] {
		return this.objetivo.filter((r) => r.applied)
	}

	get fallidos(): BatchPaymentRow[] {
		return this.objetivo.filter((r) => !!r.applyError)
	}

	/**
	 * En cuántos ARRENDAMIENTOS distintos cae el dinero.
	 *
	 * No es el número de pagos: dos rentas del mismo cliente al mismo contrato son dos pagos y un
	 * arrendamiento. La advertencia decía «mueve dinero en 2 arrendamiento(s)» con los dos yendo al
	 * mismo, y una cifra equivocada en la pantalla que pide confirmación es justo la que no se puede
	 * permitir.
	 */
	get arrendamientosTocados(): number {
		const folios = new Set<string>()
		for (const row of this.objetivo) {
			for (const letra of (row.preview?.plan ?? []) as any[]) folios.add(String(letra.leaseId ?? ''))
		}
		folios.delete('')

		return folios.size
	}

	get totalAplicado(): number {
		return this.aplicados.reduce((s, r) => s + r.amount, 0)
	}

	get terminado(): boolean {
		return !this.aplicando && this.aplicados.length > 0
	}

	/** A cuántas personas se les avisaría: clientes distintos entre los pagos que sí se aplicaron. */
	get clientesAvisados(): number {
		return new Set(this.aplicados.map((r) => r.clientId)).size
	}

	get puedeAvisar(): boolean {
		return this.terminado && !this.state.notice && !this.state.noticeAlreadySent && this.clientesAvisados > 0
	}

	async aplicar(): Promise<void> {
		if (!this.confirmado || this.aplicando) return

		this.objetivoCorrida = [...this.state.previewedRows]
		this.aplicando = true
		this.cancelado = false
		this.cancelando = false
		this.hechos = 0

		for (const row of this.objetivoCorrida) {
			if (this.cancelado) break
			if (row.applied) {
				this.hechos++
				continue
			}

			await this.aplicarUno(row)
			this.hechos++
		}

		this.aplicando = false
		this.cancelando = false
	}

	cancelar(): void {
		this.cancelado = true
		this.cancelando = true
	}

	/**
	 * Manda el aviso del lote. Una sola vez, a todos o a ninguno (D2).
	 *
	 * El botón se apaga en cuanto sale, y el backend rechaza el segundo intento aunque el botón
	 * volviera: un SMS no se puede retirar.
	 */
	avisar(): void {
		if (!this.confirmadoAviso || this.avisando || !this.puedeAvisar) return

		this.avisando = true
		this.avisoError = ''

		this.apiBatch.notify(this.state.uploadBatch).subscribe((r) => {
			this.avisando = false
			if (r.error || !r.data) {
				this.avisoError = r.msg || 'No se pudieron mandar los avisos.'
				return
			}

			this.state.notice = r.data
		})
	}

	/**
	 * Aplica un pago, refrescando su huella justo antes.
	 *
	 * **Por qué hace falta refrescarla.** El motor rechaza con 409 una aplicación cuya huella ya no
	 * corresponde al estado del arrendamiento (`applications.service.ts:837`), y eso es correcto: es
	 * lo que impide escribir un plan que se calculó sobre otro estado. Pero en un LOTE es la
	 * situación normal, no la rara — dos rentas del mismo cliente al mismo contrato son dos filas del
	 * archivo, y en cuanto la primera se aplica, la huella de la segunda ya no vale. Con las huellas
	 * del paso anterior, un lote aplicaba **el primer pago de cada arrendamiento y rechazaba el
	 * resto**. Medido en Chrome: dos pagos al mismo contrato, uno aplicado y uno rechazado.
	 *
	 * **Por qué refrescarla no afloja el candado.** La huella nueva se usa solo si el plan que
	 * devuelve —cuánto dinero y con qué advertencias bloqueantes— es el MISMO que el operador aprobó.
	 * Si cambió, no se aplica: se marca para que lo vuelva a mirar. Lo que el candado protege es que
	 * no se escriba algo distinto de lo aprobado, y eso se sigue cumpliendo; lo que deja de hacer es
	 * rechazar por que el estado cambió **por nuestra propia aplicación anterior**.
	 */
	private async aplicarUno(row: BatchPaymentRow): Promise<void> {
		const aprobado = this.huellaDelPlan(row.preview)

		const fresco = await this.pedir(this.state.buildPayload(row, false), true)
		if (fresco.error || !fresco.data) {
			row.applied = false
			row.applyError = fresco.msg || 'No se pudo recalcular el impacto antes de aplicar.'
			return
		}

		if (this.huellaDelPlan(fresco.data) !== aprobado) {
			row.preview = fresco.data
			row.confirmations = {}
			row.applied = false
			row.applyError = 'El impacto de este pago cambió desde que lo revisaste, así que no se aplicó. Vuelve al paso anterior para verlo como está ahora.'
			return
		}

		//* Se aplica con la huella RECIÉN calculada, no con la del paso anterior.
		row.preview = fresco.data
		const aplicado = await this.pedir(this.state.buildPayload(row, true), false)
		if (aplicado.error || !aplicado.data) {
			row.applied = false
			row.applyError = aplicado.msg || 'No se pudo aplicar.'
			return
		}

		row.applied = true
		row.applyError = ''
		row.pendingToApply = 0
	}

	/**
	 * Lo que hace comparable un plan con otro: **cuánto dinero y qué hay que confirmar.**
	 *
	 * No se compara el objeto entero porque cambia por cosas que no le importan a nadie —la propia
	 * huella, el orden de las letras—. Lo que el operador aprobó es una cantidad y unas advertencias;
	 * eso es lo que tiene que seguir siendo verdad.
	 */
	private huellaDelPlan(preview: any): string {
		const total = ((preview?.plan ?? []) as any[]).reduce((s, p) => s + Object.values((p?.byConcept ?? {}) as Record<string, number>).reduce((a, v) => a + (Number(v) || 0), 0), 0)
		const bloqueantes = ((preview?.warnings ?? []) as any[]).filter((w) => w.blocking).map((w) => w.code).sort()

		return `${total.toFixed(2)}|${bloqueantes.join(',')}`
	}

	private pedir(payload: any, dryRun: boolean): Promise<{ error: boolean; msg: string; data: any }> {
		return new Promise((resolve) => this.apiApplication.createPreview(payload as any, dryRun).subscribe((r) => resolve(r as any)))
	}
}
